import { Trash2, X, TrendingUp, Minus, GitBranch, Network, Repeat } from "lucide-react";
import clsx from "clsx";
import { ColorPicker } from "@/components/ui/ColorPicker";
import { useDrawingsStore } from "@/store/drawings";

const LINE_WIDTHS = [1, 2, 3] as const;

const KIND_META: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  trend:           { icon: TrendingUp, label: "Trend line"        },
  horizontal:      { icon: Minus,      label: "Horizontal line"   },
  fib_retracement: { icon: GitBranch,  label: "Fib retracement"   },
  fib_extension:   { icon: Network,    label: "Fib extension"     },
  cycle:           { icon: Repeat,     label: "Cycle lines"       },
};

interface DrawingEditPanelProps {
  drawingId: string;
}

export function DrawingEditPanel({ drawingId }: DrawingEditPanelProps) {
  const drawing = useDrawingsStore((s) => s.drawings.find((d) => d.id === drawingId));
  const updateColor = useDrawingsStore((s) => s.updateColor);
  const updateLineWidth = useDrawingsStore((s) => s.updateLineWidth);
  const removeDrawing = useDrawingsStore((s) => s.removeDrawing);
  const select = useDrawingsStore((s) => s.select);

  if (!drawing) return null;

  const meta = KIND_META[drawing.kind] ?? KIND_META.trend;
  const Icon = meta.icon;

  return (
    <div className="rounded-md border border-accent/40 bg-bg-elevated p-2.5 animate-fade-in">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-accent" />
          <span className="text-2xs font-semibold uppercase tracking-wider text-accent">
            {meta.label} selected
          </span>
        </div>
        <button
          onClick={() => select(null)}
          className="text-text-muted hover:text-text-primary"
          title="Deselect"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-2">
        {/* Color */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs text-text-secondary">Color</span>
          <ColorPicker
            value={drawing.color}
            onChange={(c) => updateColor(drawing.id, c)}
            size={20}
            popoverAlign="right"
            ariaLabel="Drawing color"
          />
        </div>

        {/* Width */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs text-text-secondary">Width</span>
          <div className="flex items-center gap-px rounded border border-border bg-bg-deep p-0.5">
            {LINE_WIDTHS.map((w) => (
              <button
                key={w}
                onClick={() => updateLineWidth(drawing.id, w)}
                className={clsx(
                  "rounded px-2 py-0.5 font-mono text-2xs transition-colors",
                  drawing.lineWidth === w
                    ? "bg-accent text-white"
                    : "text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                )}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        {/* Per-kind detail readout — TypeScript narrows correctly inside each branch */}
        <div className="rounded bg-bg-deep p-2 text-2xs font-mono tabular text-text-muted space-y-0.5">
          {drawing.kind === "trend" && (
            <>
              <div>P1 · {drawing.start.price.toFixed(2)} @ {drawing.start.ts.slice(0, 10)}</div>
              <div>P2 · {drawing.end.price.toFixed(2)} @ {drawing.end.ts.slice(0, 10)}</div>
              <div className="mt-1 text-text-secondary">
                Slope · {(((drawing.end.price - drawing.start.price) / drawing.start.price) * 100).toFixed(2)}%
              </div>
            </>
          )}
          {drawing.kind === "horizontal" && (
            <div>Price · {drawing.price.toFixed(2)}</div>
          )}
          {drawing.kind === "fib_retracement" && (
            <>
              <div>Hi · {drawing.start.price.toFixed(2)} @ {drawing.start.ts.slice(0, 10)}</div>
              <div>Lo · {drawing.end.price.toFixed(2)} @ {drawing.end.ts.slice(0, 10)}</div>
              <div className="mt-1 text-text-secondary">
                Range · {Math.abs(drawing.end.price - drawing.start.price).toFixed(2)}
              </div>
            </>
          )}
          {drawing.kind === "fib_extension" && (
            <>
              <div>P0 · {drawing.p0.price.toFixed(2)}</div>
              <div>P1 · {drawing.p1.price.toFixed(2)}</div>
              <div>P2 · {drawing.p2.price.toFixed(2)}</div>
              <div className="mt-1 text-text-secondary">
                Leg · {(drawing.p1.price - drawing.p0.price).toFixed(2)}
              </div>
            </>
          )}
          {drawing.kind === "cycle" && (
            <>
              <div>Anchor · {drawing.anchor.ts.slice(0, 10)}</div>
              <div>Second · {drawing.second.ts.slice(0, 10)}</div>
              <div className="mt-1 text-text-secondary">Lines · {drawing.count}</div>
            </>
          )}
        </div>

        {/* Delete */}
        <button
          onClick={() => removeDrawing(drawing.id)}
          className="flex w-full items-center justify-center gap-1.5 rounded border border-bear/40 bg-bear/10 px-2 py-1 text-2xs text-bear transition-colors hover:bg-bear hover:text-white"
        >
          <Trash2 className="h-3 w-3" />
          <span>Delete drawing</span>
        </button>

        {(drawing.kind === "trend" || drawing.kind === "horizontal" || drawing.kind === "fib_retracement") && (
          <div className="text-center text-[10px] text-text-muted">
            Drag the dots on the chart to reshape
          </div>
        )}
      </div>
    </div>
  );
}
